import Bowser from 'bowser';

export type BrowserInfo = {
  browser: string;
  engine: string;
  os: string;
  platform: string;
  architecture?: string;
  userAgent: string;
  languages: string;
  timezone: string;
  viewport: string;
  screen: string;
  online: boolean;
  cookiesEnabled: boolean;
  logicalCpus?: number;
  deviceMemoryGb?: number;
  maxTouchPoints?: number;
};

type ClientHints = {
  brands?: { brand: string; version: string }[];
  mobile?: boolean;
  platform?: string;
  platformVersion?: string;
  architecture?: string;
  bitness?: string;
  model?: string;
};

type NavigatorUserAgentData = {
  getHighEntropyValues?: (hints: readonly string[]) => Promise<ClientHints>;
};

const HIGH_ENTROPY_HINTS = [
  'architecture',
  'bitness',
  'fullVersionList',
  'model',
  'platformVersion',
] as const;

const UNKNOWN = 'unknown';

let cachedClientHints: ClientHints | undefined;

async function readClientHints(): Promise<ClientHints | undefined> {
  const userAgentData = (navigator as Navigator & { userAgentData?: NavigatorUserAgentData })
    .userAgentData;

  if (!userAgentData?.getHighEntropyValues) {
    return undefined;
  }

  try {
    return await userAgentData.getHighEntropyValues(HIGH_ENTROPY_HINTS);
  } catch {
    return undefined;
  }
}

function join(...parts: (string | undefined)[]): string {
  const present = parts.filter((part): part is string => part !== undefined && part !== '');
  return present.length > 0 ? present.join(' ') : UNKNOWN;
}

function describeLanguages(): string {
  const languages = navigator.languages;
  if (Array.isArray(languages) && languages.length > 0) {
    return languages.join(', ');
  }
  return navigator.language || UNKNOWN;
}

function describeScreen(): string {
  const screen = window.screen as Screen | undefined;
  return screen === undefined
    ? UNKNOWN
    : `${screen.width}x${screen.height} @ ${window.devicePixelRatio}x`;
}

function describeTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return UNKNOWN;
  }
}

function parseUserAgent(userAgent: string, hints: ClientHints | undefined) {
  if (userAgent === '') {
    return undefined;
  }

  try {
    return Bowser.parse(userAgent, hints);
  } catch {
    return undefined;
  }
}

export async function primeClientHints(): Promise<void> {
  cachedClientHints = await readClientHints();
}

export function forgetClientHints(): void {
  cachedClientHints = undefined;
}

export function collectBrowserInfo(): BrowserInfo {
  const userAgent = navigator.userAgent || '';
  const hints = cachedClientHints;
  const parsed = parseUserAgent(userAgent, hints);

  const extendedNavigator = navigator as Navigator & { deviceMemory?: number };
  const architecture = join(hints?.architecture, hints?.bitness);

  return {
    browser: join(parsed?.browser.name, parsed?.browser.version),
    engine: join(parsed?.engine.name, parsed?.engine.version),
    os: join(
      parsed?.os.name,
      parsed?.os.versionName ?? parsed?.os.version,
      hints?.platformVersion === undefined ? undefined : `(hints: ${hints.platformVersion})`,
    ),
    platform: join(parsed?.platform.type, parsed?.platform.vendor, parsed?.platform.model),
    architecture: architecture === UNKNOWN ? undefined : architecture,
    userAgent: userAgent === '' ? UNKNOWN : userAgent,
    languages: describeLanguages(),
    timezone: describeTimezone(),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: describeScreen(),
    online: navigator.onLine === true,
    cookiesEnabled: navigator.cookieEnabled === true,
    logicalCpus: navigator.hardwareConcurrency,
    deviceMemoryGb: extendedNavigator.deviceMemory,
    maxTouchPoints: navigator.maxTouchPoints,
  };
}
